import type { BlobLike } from "@pdfking/compatibility"
import type {
  GetBlockPointer,
  IsDataAvailPointer,
  PageNumber,
  PdfiumAPI,
  PdfReaderPointer,
} from "@pdfking/pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import { ArrayToIterable, IterableToArray } from "@pdfking/tfunc"
import { DefaultMinimap as M } from "@pdfking/wasm-core"
import { AutoThrow } from "./AutoThrow"

const MEM_THRESHOLD = (30 * 1024 * 1024) as Bytes

export type OpenReaderResult = {
  readonly handle: PdfReaderPointer
  readonly page_count: number
  readonly ensure_page: (page_index: number) => Promise<void>
  readonly close: () => Promise<void>
}

const OpenReaderMem = async (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>
): Promise<OpenReaderResult> => {
  const handle = AutoThrow(
    await IterableToArray(stream)
      .then(BlobArray)
      .then(BlobToUint8)
      .then(data => api.open_mem(data))
  )
  const count = Number(await api.page_count(handle))

  return {
    handle,
    page_count: count,
    ensure_page: async () => {},
    close: async () => {
      AutoThrow(await api.close_mem(handle))
    },
  }
}

const OpenReaderStream = async (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>,
  size: Bytes
): Promise<OpenReaderResult> => {
  const blob = BlobArray()
  const iter = ArrayToIterable(stream)
  const { instance } = api

  const pump = (): Promise<boolean> =>
    iter.next().then(({ done, value }) => {
      if (value) blob.push(value)
      return !done
    })

  const heapu8 = () =>
    new Uint8Array((instance.exports[M.memory] as WebAssembly.Memory).buffer)

  const getBlock = (
    _param: number,
    position: number,
    bufPtr: number,
    sz: number
  ): number => {
    if (position + sz > blob.size) return 0
    const data = new Uint8Array(
      blob.slice(position, position + sz).arrayBufferSync()
    )
    heapu8().set(data, bufPtr)
    return 1
  }

  const isDataAvail = (_pThis: number, offset: number, sz: number): number =>
    offset + sz <= blob.size ? 1 : 0

  const getBlockCb = instance.addCallback(getBlock, "iiiii")
  const isDataAvailCb = instance.addCallback(isDataAvail, "iiii")

  const cleanup = () => {
    getBlockCb.free()
    isDataAvailCb.free()
    blob.clear()
  }

  try {
    const handle = AutoThrow(
      await api.open(
        size,
        getBlockCb.pointer as unknown as GetBlockPointer,
        isDataAvailCb.pointer as unknown as IsDataAvailPointer
      )
    )

    for (;;) {
      if (AutoThrow(await api.doc_avail(handle))) break
      if (!(await pump())) break
    }

    const count = Number(await api.page_count(handle))

    const ensure_page = async (page_index: number): Promise<void> => {
      for (;;) {
        if (AutoThrow(await api.page_avail(handle, page_index as PageNumber)))
          break
        if (!(await pump())) break
      }
    }

    return {
      handle,
      page_count: count,
      ensure_page,
      close: async () => {
        AutoThrow(await api.close(handle))
        cleanup()
      },
    }
  } catch (e) {
    cleanup()
    throw e
  }
}

export const OpenReader = (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>,
  size: Bytes
): Promise<OpenReaderResult> =>
  size <= MEM_THRESHOLD
    ? OpenReaderMem(api, stream)
    : OpenReaderStream(api, stream, size)
